// src/components/Budget/SharedCategoryDialog.js
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Tabs,
  Tab,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  Stack,
  IconButton,
  InputAdornment,
  Alert,
  CircularProgress,
  useTheme,
  alpha,
  Paper,
} from '@mui/material';
import {
  Close as CloseIcon,
  Add as AddIcon,
  Login as LoginIcon,
  Info as InfoIcon,
  GroupAdd as GroupAddIcon,
} from '@mui/icons-material';
import { useSharedCategory } from '../../contexts/SharedCategoryContext';
import { InviteCodeDisplay } from './InviteCodeDisplay';

function TabPanel({ children, value, index, ...other }) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`shared-category-tabpanel-${index}`}
      aria-labelledby={`shared-category-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
}

export const SharedCategoryDialog = ({ open, onClose, onSuccess }) => {
  const theme = useTheme();
  const { createSharedCategory, joinSharedCategory, loading } = useSharedCategory();
  
  const [tabValue, setTabValue] = useState(0);
  const [error, setError] = useState('');
  
  // Create form state
  const [createForm, setCreateForm] = useState({
    name: '',
    budgetMode: 'shared',
    sharedLimit: '',
    transparencyLevel: 'full',
  });
  const [createErrors, setCreateErrors] = useState({});
  
  // Join form state
  const [inviteCode, setInviteCode] = useState('');
  const [joinError, setJoinError] = useState('');
  
  // Invite code display state
  const [showInviteCode, setShowInviteCode] = useState(false);
  const [createdCategory, setCreatedCategory] = useState(null);

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
    setError('');
    setJoinError('');
    setCreateErrors({});
  };

  const handleCreateChange = (field) => (event) => {
    setCreateForm({ ...createForm, [field]: event.target.value });
    // Clear error for this field
    if (createErrors[field]) {
      setCreateErrors({ ...createErrors, [field]: '' });
    }
  };

  const validateCreateForm = () => {
    const errors = {};
    
    if (!createForm.name.trim()) {
      errors.name = 'Category name is required';
    }
    
    if (!createForm.sharedLimit || parseFloat(createForm.sharedLimit) <= 0) {
      errors.sharedLimit = 'Budget limit must be greater than 0';
    }
    
    setCreateErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleCreate = async () => {
    if (!validateCreateForm()) return;
    
    setError('');
    
    try {
      const result = await createSharedCategory({
        name: createForm.name,
        budgetMode: createForm.budgetMode,
        sharedLimit: parseFloat(createForm.sharedLimit),
        transparencyLevel: createForm.transparencyLevel,
      });
      
      // Show invite code display
      setCreatedCategory(result.sharedCategory);
      setShowInviteCode(true);
      
      // Reset form
      setCreateForm({
        name: '',
        budgetMode: 'shared',
        sharedLimit: '',
        transparencyLevel: 'full',
      });
      
      // Call success callback
      if (onSuccess) onSuccess(result.sharedCategory);
      
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to create shared category');
    }
  };

  const handleJoin = async () => {
    if (!inviteCode.trim()) {
      setJoinError('Please enter an invite code');
      return;
    }
    
    setJoinError('');
    
    try {
      const result = await joinSharedCategory(inviteCode.trim().toUpperCase());
      
      // Success - show message and close
      if (onSuccess) onSuccess(result.sharedCategory);
      
      // Reset and close
      setInviteCode('');
      onClose();
      
    } catch (err) {
      const errorMsg = err.message || 'Failed to join shared category';
      setJoinError(errorMsg);
    }
  };

  const handleInviteCodeClose = () => {
    setShowInviteCode(false);
    setCreatedCategory(null);
    onClose();
  };

  const handleInviteCodeChange = (e) => {
    // Auto-uppercase and limit to 6 characters
    const value = e.target.value.toUpperCase().slice(0, 6);
    setInviteCode(value);
    if (joinError) setJoinError('');
  };

  return (
    <>
      <Dialog
        open={open && !showInviteCode}
        onClose={onClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 3 },
        }}
      >
        <DialogTitle>
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Stack direction="row" alignItems="center" spacing={1}>
              <GroupAddIcon color="primary" />
              <Typography variant="h6" fontWeight="600">
                Shared Budget Category
              </Typography>
            </Stack>
            <IconButton onClick={onClose} size="small">
              <CloseIcon />
            </IconButton>
          </Stack>
        </DialogTitle>

        <Box sx={{ borderBottom: 1, borderColor: 'divider', px: 3 }}>
          <Tabs value={tabValue} onChange={handleTabChange} aria-label="shared category tabs">
            <Tab label="Create New" icon={<AddIcon />} iconPosition="start" />
            <Tab label="Join Existing" icon={<LoginIcon />} iconPosition="start" />
          </Tabs>
        </Box>

        <DialogContent>
          {/* Create Tab */}
          <TabPanel value={tabValue} index={0}>
            <Stack spacing={3}>
              {error && (
                <Alert severity="error" onClose={() => setError('')}>
                  {error}
                </Alert>
              )}

              <TextField
                label="Category Name"
                placeholder="e.g., Household Expenses"
                value={createForm.name}
                onChange={handleCreateChange('name')}
                error={!!createErrors.name}
                helperText={createErrors.name}
                fullWidth
                autoFocus
              />

              <TextField
                label="Budget Limit"
                type="number"
                value={createForm.sharedLimit}
                onChange={handleCreateChange('sharedLimit')}
                error={!!createErrors.sharedLimit}
                helperText={createErrors.sharedLimit || 'Monthly budget for this category'}
                fullWidth
                InputProps={{
                  startAdornment: <InputAdornment position="start">$</InputAdornment>,
                }}
              />

              <FormControl fullWidth>
                <InputLabel>Budget Mode</InputLabel>
                <Select
                  value={createForm.budgetMode}
                  onChange={handleCreateChange('budgetMode')}
                  label="Budget Mode"
                >
                  <MenuItem value="shared">
                    <Stack>
                      <Typography variant="body2" fontWeight="500">
                        Shared Limit
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        All members share the same budget pool
                      </Typography>
                    </Stack>
                  </MenuItem>
                  <MenuItem value="individual">
                    <Stack>
                      <Typography variant="body2" fontWeight="500">
                        Individual Limits
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Each member has their own budget allocation
                      </Typography>
                    </Stack>
                  </MenuItem>
                </Select>
                <FormHelperText>
                  Choose how the budget should be divided among members
                </FormHelperText>
              </FormControl>

              <FormControl fullWidth>
                <InputLabel>Transparency Level</InputLabel>
                <Select
                  value={createForm.transparencyLevel}
                  onChange={handleCreateChange('transparencyLevel')}
                  label="Transparency Level"
                >
                  <MenuItem value="full">
                    <Stack>
                      <Typography variant="body2" fontWeight="500">
                        Full Transparency
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Everyone sees all transactions
                      </Typography>
                    </Stack>
                  </MenuItem>
                  <MenuItem value="private">
                    <Stack>
                      <Typography variant="body2" fontWeight="500">
                        Private
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Members only see their own transactions
                      </Typography>
                    </Stack>
                  </MenuItem>
                </Select>
                <FormHelperText>
                  Control what members can see
                </FormHelperText>
              </FormControl>

              <Paper
                elevation={0}
                sx={{
                  p: 2,
                  backgroundColor: alpha(theme.palette.info.main, 0.1),
                  border: `1px solid ${alpha(theme.palette.info.main, 0.3)}`,
                  borderRadius: 1,
                }}
              >
                <Stack direction="row" spacing={1.5}>
                  <InfoIcon color="info" fontSize="small" />
                  <Typography variant="body2" color="text.secondary">
                    After creating, you'll receive an invite code to share with others
                  </Typography>
                </Stack>
              </Paper>
            </Stack>
          </TabPanel>

          {/* Join Tab */}
          <TabPanel value={tabValue} index={1}>
            <Stack spacing={3}>
              {joinError && (
                <Alert severity="error" onClose={() => setJoinError('')}>
                  {joinError}
                </Alert>
              )}

              <TextField
                label="Invite Code"
                placeholder="Enter 6-character code"
                value={inviteCode}
                onChange={handleInviteCodeChange}
                error={!!joinError}
                helperText="Ask the category owner for the invite code"
                fullWidth
                autoFocus
                inputProps={{
                  maxLength: 6,
                  style: {
                    textTransform: 'uppercase',
                    letterSpacing: '0.2em',
                    fontFamily: 'monospace',
                    fontSize: '1.2rem',
                    textAlign: 'center',
                  },
                }}
              />

              <Paper
                elevation={0}
                sx={{
                  p: 2,
                  backgroundColor: alpha(theme.palette.info.main, 0.1),
                  border: `1px solid ${alpha(theme.palette.info.main, 0.3)}`,
                  borderRadius: 1,
                }}
              >
                <Stack direction="row" spacing={1.5}>
                  <InfoIcon color="info" fontSize="small" />
                  <Stack spacing={0.5}>
                    <Typography variant="body2" fontWeight="500">
                      How to join:
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      1. Get the 6-character invite code from the category owner
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      2. Enter it above to join the shared category
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      3. Start tracking expenses together!
                    </Typography>
                  </Stack>
                </Stack>
              </Paper>
            </Stack>
          </TabPanel>
        </DialogContent>

        <DialogActions sx={{ p: 3 }}>
          <Button onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          {tabValue === 0 ? (
            <Button
              variant="contained"
              onClick={handleCreate}
              disabled={loading}
              startIcon={loading ? <CircularProgress size={20} /> : <AddIcon />}
            >
              Create Category
            </Button>
          ) : (
            <Button
              variant="contained"
              onClick={handleJoin}
              disabled={loading || inviteCode.length !== 6}
              startIcon={loading ? <CircularProgress size={20} /> : <LoginIcon />}
            >
              Join Category
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Invite Code Display Dialog */}
      {createdCategory && (
        <InviteCodeDisplay
          open={showInviteCode}
          onClose={handleInviteCodeClose}
          inviteCode={createdCategory.inviteCode}
          expiryDate={createdCategory.inviteCodeExpiry}
        />
      )}
    </>
  );
};

SharedCategoryDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSuccess: PropTypes.func,
};

export default SharedCategoryDialog;
